import AddIcon from "@atlaskit/icon/core/add";
import ArchiveIcon from "@atlaskit/icon/core/archive-box";
import CommentIcon from "@atlaskit/icon/core/comment";
import EditIcon from "@atlaskit/icon/core/edit";
import EyeOpenIcon from "@atlaskit/icon/core/eye-open";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import OnCallIcon from "@atlaskit/icon/core/on-call";
import RoadmapIcon from "@atlaskit/icon/core/roadmap";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import {ScheduleCallIcon} from "~/components/svgs";
import {token} from "@atlaskit/tokens";
import type {NewCoreIconProps} from "@atlaskit/icon/base-new";
import {useMemo, useState, type ReactNode, type Ref} from "react";
import {twJoin} from "tailwind-merge";
import type {OttopilotListCardLayout} from "~/components/ottopilotDataViewer/view";
import {TableRowActionsMenuDrawer} from "~/components/table/TableRowActionsMenuDrawer";
import {
    getTableRowActionsMenuHandler,
    TABLE_ROW_ACTIONS_MENU_ITEMS,
} from "~/components/table/tableRowActionsMenuItems";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeDropdownMenu} from "@vesta/ananke/components/anankeDropdownMenu";
import type {
    TableRowActionsConfig,
    TableRowActionsIconComponent,
    TableRowActionsMenuItem,
    TableRowActionsToolbarItem,
} from "~/components/table/tableRowActions.types";

const listCardBrandIconColor = (isDisabled: boolean) =>
    isDisabled === true ? token("color.icon.disabled") : token("color.icon.brand");

const listCardMoreMenuIconColor = (isDisabled: boolean) =>
    isDisabled === true ? token("color.icon.disabled") : token("color.icon");

function wrapIconWithBrandColor(Icon: TableRowActionsIconComponent): TableRowActionsIconComponent {
    function BrandIcon(props: NewCoreIconProps) {
        return (
            <Icon
                {...props}
                color={props.color ?? listCardBrandIconColor(false)}
                label={props.label ?? ""}
            />
        );
    }
    return BrandIcon;
}

function getListCardBrandMenuItemIcon(item: TableRowActionsMenuItem, isDisabled: boolean): ReactNode {
    const color = listCardMoreMenuIconColor(isDisabled);

    switch (item) {
        case "profile":
            return <LinkExternalIcon label="" size="small" color={color} />;
        case "disposition":
            return <RoadmapIcon label="" size="small" color={color} />;
        case "edit":
            return <EditIcon label="" size="small" color={color} />;
        case "view":
            return <EyeOpenIcon label="" size="small" color={color} />;
        case "create":
            return <AddIcon label="" size="small" color={color} />;
        case "logCall":
            return <OnCallIcon label="" size="small" color={color} />;
        case "notes":
            return <CommentIcon label="" size="small" color={color} />;
        case "scheduleCall":
            return <ScheduleCallIcon size={20} color={color} />;
        case "archive":
            return <ArchiveIcon label="" size="small" color={color} />;
    }
}

function withListCardBrandActionsConfig<TRow>(config: TableRowActionsConfig<TRow>): TableRowActionsConfig<TRow> {
    return {
        ...config,
        useBrandToolbarIconColors: true,
        getToolbarItemIcon: (item) => {
            if (item === "more") {
                return config.getToolbarItemIcon?.(item);
            }
            const existing = config.getToolbarItemIcon?.(item);
            if (existing !== undefined) {
                return wrapIconWithBrandColor(existing);
            }
            if (
                item === "profile"
                || item === "disposition"
                || item === "edit"
                || item === "view"
                || item === "logCall"
                || item === "notes"
                || item === "archive"
            ) {
                return wrapIconWithBrandColor(MOBILE_LIST_FOOTER_ICONS[item]);
            }
            return undefined;
        },
        getMenuItemIcon: (item, isDisabled) => {
            const existing = config.getMenuItemIcon?.(item, isDisabled);
            if (existing !== undefined) {
                return existing;
            }
            return getListCardBrandMenuItemIcon(item, isDisabled);
        },
    };
}

const MOBILE_LIST_FOOTER_LABELS: Record<Exclude<TableRowActionsToolbarItem, "more">, string> = {
    profile: "Profile",
    disposition: "Dispose",
    edit: "Edit",
    view: "View",
    logCall: "Log a call",
    notes: "Notes",
    archive: "Archive",
};

const MOBILE_LIST_FOOTER_ICONS = {
    profile: LinkExternalIcon,
    disposition: RoadmapIcon,
    edit: EditIcon,
    view: EyeOpenIcon,
    logCall: OnCallIcon,
    notes: CommentIcon,
    archive: ArchiveIcon,
} as const;

function ListCardLabeledAction({
    icon,
    label,
    isDisabled,
    onClick,
    compact = false,
}: {
    icon: ReactNode;
    label: string;
    isDisabled?: boolean;
    onClick: () => void;
    compact?: boolean;
}) {
    return (
        <button
            type="button"
            disabled={isDisabled === true}
            onClick={(event) => {
                event.stopPropagation();
                if (isDisabled === true) {
                    return;
                }
                onClick();
            }}
            className={twJoin(
                "grid grid-rows-[max-content_max-content] justify-items-center bg-transparent border-0 p-0 disabled:cursor-not-allowed disabled:opacity-50",
                compact === true ? "gap-y-ds-space-025" : "gap-y-ds-space-050",
            )}
        >
            <span
                className={twJoin(
                    "grid place-items-center",
                    compact === true
                        ? "h-5 w-5 [&_svg]:h-5 [&_svg]:w-5"
                        : "h-6 w-6 [&_svg]:h-6 [&_svg]:w-6",
                )}
            >
                {icon}
            </span>
            <span
                style={{
                    color:
                        isDisabled === true
                            ? token("color.text.disabled")
                            : token("color.text"),
                    fontFamily: "Inter",
                    fontSize: "var(--Font-Size-Body-S, 12px)",
                    fontStyle: "normal",
                    fontWeight: "var(--Font-Weight-Body-Regular, 400)",
                    lineHeight: "var(--Line-Height-Body-S, 16px)",
                }}
            >
                {label}
            </span>
        </button>
    );
}

function ListCardRowActionsLabeledFooter<TRow>({
    row,
    config,
    layout,
    stacked,
}: {
    row: TRow;
    config: TableRowActionsConfig<TRow>;
    layout: OttopilotListCardLayout;
    stacked: boolean;
}) {
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const canEdit = config.canEdit?.(row) ?? true;

    const isToolbarVisible = (item: TableRowActionsToolbarItem) =>
        config.isToolbarItemVisible?.(item, row) ?? true;

    const isToolbarDisabled = (item: TableRowActionsToolbarItem) =>
        config.isToolbarItemDisabled?.(item, row) === true;

    const isMenuVisible = (item: TableRowActionsMenuItem) => config.isMenuItemVisible?.(item, row) ?? true;

    const moreMenuItems = (config.moreMenu ?? []).filter(isMenuVisible);

    const iconColor = listCardBrandIconColor;
    const useStackedFooter = stacked === true || layout !== "desktop";

    const renderToolbarAction = (item: Exclude<TableRowActionsToolbarItem, "more">) => {
        if (isToolbarVisible(item) !== true) {
            return null;
        }

        const Icon = MOBILE_LIST_FOOTER_ICONS[item];
        const label =
            item === "profile"
                ? (config.menuItemLabels?.profile ?? MOBILE_LIST_FOOTER_LABELS[item])
                : (config.toolbarItemLabels?.[item] ?? MOBILE_LIST_FOOTER_LABELS[item]);

        if (item === "profile") {
            const isDisabled = isToolbarDisabled("profile");
            return (
                <ListCardLabeledAction
                    key={item}
                    compact={useStackedFooter}
                    icon={<Icon label="" size="small" color={iconColor(isDisabled)} />}
                    label={label}
                    isDisabled={isDisabled}
                    onClick={() => {
                        if (isDisabled === true) {
                            return;
                        }
                        config.onProfile?.(row);
                    }}
                />
            );
        }

        if (item === "disposition") {
            const isDisabled = isToolbarDisabled("disposition");
            return (
                <ListCardLabeledAction
                    key={item}
                    compact={useStackedFooter}
                    icon={<Icon label="" size="small" color={iconColor(isDisabled)} />}
                    label={label}
                    isDisabled={isDisabled}
                    onClick={() => {
                        if (isDisabled === true) {
                            return;
                        }
                        config.onDisposition?.(row);
                    }}
                />
            );
        }

        if (item === "edit") {
            const isDisabled = canEdit !== true;
            return (
                <ListCardLabeledAction
                    key={item}
                    compact={useStackedFooter}
                    icon={<Icon label="" size="small" color={iconColor(isDisabled)} />}
                    label={label}
                    isDisabled={isDisabled}
                    onClick={() => {
                        if (isDisabled === true) {
                            return;
                        }
                        config.onEdit?.(row);
                    }}
                />
            );
        }

        if (item === "logCall") {
            const isDisabled = isToolbarDisabled("logCall");
            return (
                <ListCardLabeledAction
                    key={item}
                    compact={useStackedFooter}
                    icon={<Icon label="" size="small" color={iconColor(isDisabled)} />}
                    label={label}
                    isDisabled={isDisabled}
                    onClick={() => {
                        if (isDisabled === true) {
                            return;
                        }
                        config.onLogCall?.(row);
                    }}
                />
            );
        }

        if (item === "notes") {
            const isDisabled = isToolbarDisabled("notes");
            return (
                <ListCardLabeledAction
                    key={item}
                    compact={useStackedFooter}
                    icon={<Icon label="" size="small" color={iconColor(isDisabled)} />}
                    label={label}
                    isDisabled={isDisabled}
                    onClick={() => {
                        if (isDisabled === true) {
                            return;
                        }
                        config.onNotes?.(row);
                    }}
                />
            );
        }

        if (item === "archive") {
            const isDisabled = isToolbarDisabled("archive");
            return (
                <ListCardLabeledAction
                    key={item}
                    compact={useStackedFooter}
                    icon={<Icon label="" size="small" color={iconColor(isDisabled)} />}
                    label={label}
                    isDisabled={isDisabled}
                    onClick={() => {
                        if (isDisabled === true) {
                            return;
                        }
                        config.onArchive?.(row);
                    }}
                />
            );
        }

        const isDisabled = isToolbarDisabled("view");
        return (
            <ListCardLabeledAction
                key={item}
                compact={useStackedFooter}
                icon={<Icon label="" size="small" color={iconColor(isDisabled)} />}
                label={label}
                isDisabled={isDisabled}
                onClick={() => {
                    if (isDisabled === true) {
                        return;
                    }
                    config.onView?.(row);
                }}
            />
        );
    };

    const isMoreTriggerDisabled =
        moreMenuItems.length === 0 ||
        moreMenuItems.every((item) => config.isMenuItemDisabled?.(item, row) === true);

    const runMenuAction = (item: TableRowActionsMenuItem) => {
        if (config.isMenuItemDisabled?.(item, row) === true) {
            return;
        }
        const handler = getTableRowActionsMenuHandler(item, config);
        if (handler === undefined) {
            return;
        }
        handler(row);
        setMoreMenuOpen(false);
    };

    const moreAction =
        isToolbarVisible("more") !== true || moreMenuItems.length === 0 ? null : useStackedFooter !== true ? (
            <AnankeDropdownMenu
                isOpen={moreMenuOpen}
                onOpenChange={({isOpen}) => {
                    setMoreMenuOpen(isOpen);
                }}
                placement="bottom-end"
                shouldRenderToParent={false}
                trigger={({triggerRef, ...triggerProps}) => (
                    <button
                        type="button"
                        ref={triggerRef as Ref<HTMLButtonElement>}
                        {...triggerProps}
                        disabled={isMoreTriggerDisabled === true}
                        onClick={(event) => {
                            event.stopPropagation();
                            triggerProps.onClick?.(event);
                        }}
                        onPointerDown={(event) => {
                            event.stopPropagation();
                        }}
                        className="grid grid-rows-[max-content_max-content] justify-items-center gap-y-ds-space-050 bg-transparent border-0 p-0 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <span className="grid h-6 w-6 place-items-center [&_svg]:h-6 [&_svg]:w-6">
                            <ShowMoreHorizontalIcon
                                label=""
                                size="small"
                                color={iconColor(isMoreTriggerDisabled)}
                            />
                        </span>
                        <span
                            style={{
                                color: token("color.text.subtle"),
                                fontFamily: "Inter",
                                fontSize: "var(--Font-Size-Body-S, 12px)",
                                fontStyle: "normal",
                                fontWeight: "var(--Font-Weight-Body-Regular, 400)",
                                lineHeight: "var(--Line-Height-Body-S, 16px)",
                            }}
                        >
                            More
                        </span>
                    </button>
                )}
            >
                <AnankeDropdownItemGroup>
                    {moreMenuItems.map((itemId) => {
                        const item = TABLE_ROW_ACTIONS_MENU_ITEMS[itemId];
                        if (item === undefined) {
                            return null;
                        }
                        const label = config.menuItemLabels?.[itemId] ?? item.label;
                        const isDisabled = config.isMenuItemDisabled?.(itemId, row) === true;
                        const menuIcon =
                            config.getMenuItemIcon?.(itemId, isDisabled) ??
                            getListCardBrandMenuItemIcon(itemId, isDisabled);

                        return (
                            <AnankeDropdownItem
                                key={itemId}
                                elemBefore={menuIcon}
                                isDisabled={isDisabled}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    runMenuAction(itemId);
                                }}
                            >
                                {label}
                            </AnankeDropdownItem>
                        );
                    })}
                </AnankeDropdownItemGroup>
            </AnankeDropdownMenu>
        ) : (
            <>
                <ListCardLabeledAction
                    compact={useStackedFooter}
                    icon={
                        <ShowMoreHorizontalIcon
                            label=""
                            size="small"
                            color={iconColor(isMoreTriggerDisabled)}
                        />
                    }
                    label="More"
                    isDisabled={isMoreTriggerDisabled}
                    onClick={() => {
                        if (isMoreTriggerDisabled === true) {
                            return;
                        }
                        setMoreMenuOpen(true);
                    }}
                />
                <TableRowActionsMenuDrawer
                    open={moreMenuOpen}
                    setOpen={setMoreMenuOpen}
                    row={row}
                    config={config}
                    menuItemIds={moreMenuItems}
                />
            </>
        );

    const toolbarActions = config.toolbar.map((item) =>
        item === "more" ? (
            <span key="more" className="contents">
                {moreAction}
            </span>
        ) : (
            renderToolbarAction(item)
        ),
    );

    if (useStackedFooter !== true) {
        return (
            <div
                className="grid min-w-0 w-full grid-flow-col auto-cols-max items-center justify-end justify-self-end gap-x-ds-space-150"
                data-list-card-footer=""
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onPointerDown={(event) => {
                    event.stopPropagation();
                }}
            >
                {toolbarActions}
            </div>
        );
    }

    const stackedColumnCount = Math.max(
        1,
        config.toolbar.filter((item) => {
            if (item === "more") {
                return moreAction !== null;
            }
            return isToolbarVisible(item) === true;
        }).length,
    );

    return (
        <>
            <div className="h-px w-full" style={{background: token("color.border")}} />
            <div
                className={twJoin(
                    "grid w-full justify-items-center py-ds-space-100",
                    layout === "mobile" ? "px-ds-space-100" : "px-ds-space-200",
                )}
                style={{gridTemplateColumns: `repeat(${stackedColumnCount}, minmax(0, 1fr))`}}
                data-list-card-footer=""
                onClick={(event) => {
                    event.stopPropagation();
                }}
                onPointerDown={(event) => {
                    event.stopPropagation();
                }}
            >
                {toolbarActions}
            </div>
        </>
    );
}

/** List card footer: same toolbar + more menu as the journeys table row actions column. */
export function ListCardRowActions<TRow>({
    row,
    config,
    layout = "mobile",
    stacked = false,
}: {
    row: TRow;
    config: TableRowActionsConfig<TRow>;
    layout?: OttopilotListCardLayout;
    /** When true, always render the full-width labeled action bar (profile event cards). */
    stacked?: boolean;
}) {
    const brandedConfig = useMemo(() => withListCardBrandActionsConfig(config), [config]);

    return (
        <ListCardRowActionsLabeledFooter
            row={row}
            config={brandedConfig}
            layout={layout}
            stacked={stacked}
        />
    );
}
